import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';

const FilterToolbar = ({ onFiltersChange, onBulkAction }) => {
  const [filters, setFilters] = useState({
    status: 'all',
    vertical: 'all',
    priority: 'all',
    dateRange: '7d',
    searchQuery: ''
  });

  const [selectedItems, setSelectedItems] = useState([]);
  const [showBulkActions, setShowBulkActions] = useState(false);

  const statusOptions = [
    { value: 'all', label: 'All Status' },
    { value: 'pending', label: 'Pending Review' },
    { value: 'verified', label: 'Verified' },
    { value: 'suspended', label: 'Suspended' },
    { value: 'rejected', label: 'Rejected' }
  ];

  const verticalOptions = [
    { value: 'all', label: 'All Verticals' },
    { value: 'food', label: 'Food & Delivery' },
    { value: 'services', label: 'Home Services' },
    { value: 'property', label: 'Property' },
    { value: 'tours', label: 'Tours & Travel' }
  ];

  const priorityOptions = [
    { value: 'all', label: 'All Priorities' },
    { value: 'high', label: 'High Priority' },
    { value: 'medium', label: 'Medium Priority' },
    { value: 'low', label: 'Low Priority' }
  ];

  const dateRangeOptions = [
    { value: '1d', label: 'Last 24 hours' },
    { value: '7d', label: 'Last 7 days' },
    { value: '30d', label: 'Last 30 days' },
    { value: '90d', label: 'Last 90 days' },
    { value: 'custom', label: 'Custom Range' }
  ];

  const bulkActions = [
    { id: 'approve', label: 'Approve Selected', icon: 'CheckCircle', variant: 'success' },
    { id: 'reject', label: 'Reject Selected', icon: 'XCircle', variant: 'destructive' },
    { id: 'suspend', label: 'Suspend Selected', icon: 'Pause', variant: 'warning' },
    { id: 'export', label: 'Export Selected', icon: 'Download', variant: 'outline' }
  ];

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    onFiltersChange?.(newFilters);
  };

  const handleBulkActionClick = (actionId) => {
    onBulkAction?.(actionId, selectedItems);
    setSelectedItems([]);
    setShowBulkActions(false);
  };

  const clearAllFilters = () => {
    const defaultFilters = {
      status: 'all',
      vertical: 'all',
      priority: 'all',
      dateRange: '7d',
      searchQuery: ''
    };
    setFilters(defaultFilters);
    onFiltersChange?.(defaultFilters);
  };

  const hasActiveFilters = Object.values(filters)?.some(value => 
    value !== 'all' && value !== '7d' && value !== ''
  );

  return (
    <div className="bg-card border border-border rounded-lg p-4 space-y-4">
      {/* Main Filter Row */}
      <div className="flex flex-wrap items-center gap-4">
        {/* Search */}
        <div className="flex-1 min-w-64">
          <Input
            type="search"
            placeholder="Search partners by name or business..."
            value={filters?.searchQuery}
            onChange={(e) => handleFilterChange('searchQuery', e?.target?.value)}
            className="w-full"
          />
        </div>

        {/* Status Filter */}
        <Select
          options={statusOptions}
          value={filters?.status}
          onChange={(value) => handleFilterChange('status', value)}
          placeholder="Filter by status"
          className="w-40"
        />

        {/* Vertical Filter */}
        <Select
          options={verticalOptions}
          value={filters?.vertical}
          onChange={(value) => handleFilterChange('vertical', value)}
          placeholder="Filter by vertical"
          className="w-40"
        />

        {/* Priority Filter */}
        <Select
          options={priorityOptions}
          value={filters?.priority}
          onChange={(value) => handleFilterChange('priority', value)}
          placeholder="Filter by priority"
          className="w-40"
        />

        {/* Date Range Filter */}
        <Select
          options={dateRangeOptions}
          value={filters?.dateRange}
          onChange={(value) => handleFilterChange('dateRange', value)}
          placeholder="Date range"
          className="w-40"
        />

        {/* Clear Filters */}
        {hasActiveFilters && (
          <Button variant="ghost" onClick={clearAllFilters}>
            <Icon name="X" size={16} className="mr-2" />
            Clear
          </Button>
        )}
      </div>
      {/* Bulk Actions Row */}
      {showBulkActions && selectedItems?.length > 0 && (
        <div className="flex items-center justify-between p-3 bg-primary/5 border border-primary/20 rounded-lg">
          <div className="flex items-center space-x-3">
            <Icon name="CheckSquare" size={20} className="text-primary" />
            <span className="text-sm font-medium text-foreground">
              {selectedItems?.length} partner{selectedItems?.length !== 1 ? 's' : ''} selected
            </span>
          </div>

          <div className="flex items-center space-x-2">
            {bulkActions?.map((action) => (
              <Button
                key={action?.id}
                variant={action?.variant}
                size="sm"
                onClick={() => handleBulkActionClick(action?.id)}
              >
                <Icon name={action?.icon} size={16} className="mr-2" />
                {action?.label}
              </Button>
            ))}
            
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => {
                setSelectedItems([]);
                setShowBulkActions(false);
              }}
            >
              <Icon name="X" size={16} />
            </Button>
          </div>
        </div>
      )}
      {/* Quick Stats */}
      <div className="flex items-center justify-between text-sm text-muted-foreground">
        <div className="flex items-center space-x-6">
          <span>Total Partners: 1,247</span>
          <span>Pending Review: 23</span>
          <span>Active: 1,156</span>
          <span>Suspended: 68</span>
        </div>
        
        <div className="flex items-center space-x-2">
          <Button variant="ghost" size="sm">
            <Icon name="RefreshCw" size={16} className="mr-2" />
            Refresh
          </Button>
          <Button variant="outline" size="sm">
            <Icon name="Download" size={16} className="mr-2" />
            Export
          </Button>
        </div>
      </div>
    </div>
  );
};

export default FilterToolbar;